import { Link, useLocation } from "react-router-dom";
import { repoFromPath, repoRelativePath } from "../data/repo.js";
import { SystemPage, SystemSection } from "./SystemPage.js";
import { useSession } from "../data/usePage.js";

/**
 * `/_tools` — the header's one non-content entry, a hub that fans out to the
 * app's surfaces: a "Browse" section (data / local / apps) for everyone, a
 * "Repos" section (each repo's git surface, plus the config that declares
 * them), and an "Admin" section (access / status / cleanup / healthz) shown
 * only to admins.
 *
 * Hidden repos are out of the sidebar, so this page is where the two the server
 * hides — the reference docs and the config repo — are reached. A repo an
 * operator hides is *not* linked here: hiding it is the whole point, and only
 * these two have a place in the app worth naming.
 */
export function ToolsView() {
  const session = useSession();
  const s = session.status === "ready" ? session.data : null;
  const repos = (s?.repos ?? []).filter((r) => !r.hidden);
  const hasDwdocs = (s?.repos ?? []).some((r) => r.name === "dwdocs");
  // The content page the header's "tools" link was clicked from (Header passes
  // it as router state) — the target of "share this page".
  const from = (useLocation().state as { from?: string } | null)?.from ?? "";
  const fromRepo = from && !from.startsWith("/_") ? repoFromPath(from, repos[0]?.name ?? "") : "";
  const fromPath = fromRepo ? repoRelativePath(from, fromRepo) : "";
  // A trailing "/" (a directory URL) shares the directory.
  const shareHref = (repo: string, path: string) =>
    `/_share?${new URLSearchParams({
      repo,
      path: path.replace(/\/+$/, ""),
      ...(path === "" || path.endsWith("/") ? { dir: "1" } : {}),
    }).toString()}`;
  return (
    <SystemPage title="Tools">
      <SystemSection title="Tools">
        <ul className="dw-tools-list">
          <li>
            <Link to="/_data">data</Link> — data sources and their live status.
          </li>
          <li>
            <Link to="/_local">local</Link> — the local <code>npx datawiki</code>{" "}
            client and this checkout's setup.
          </li>
          <li>
            <Link to="/_vss">semantic search</Link> — find pages by meaning from a
            document (paste a transcript). Also <code>datawiki related</code>.
          </li>
          {s?.user && (
            <li>
              <Link to="/_apps">apps</Link> — connected apps and API tokens.
            </li>
          )}
          {s?.user && fromRepo && (
            <li>
              <Link to={shareHref(fromRepo, fromPath)}>share this page</Link> — give
              a guest email read access to <code>/{fromRepo}/{fromPath}</code>
              {fromPath === "" || fromPath.endsWith("/") ? " (the whole directory)" : ""}.
            </li>
          )}
          {s?.user && (
            <li>
              <Link to="/_share">share a page</Link> — pick any page or directory
              and share it with a guest email.
            </li>
          )}
          {hasDwdocs && (
            <li>
              <Link to="/dwdocs/">reference docs</Link> — datawiki's own docs for
              this build: fences, dwsql, editing, access. Read-only.
            </li>
          )}
        </ul>
      </SystemSection>
      <SystemSection title="Repos">
        <ul className="dw-tools-list">
          {repos.map((r) => (
            <li key={r.name}>
              <Link to={`/_repo/${r.name}`}>{r.name}</Link> — branch, drafts, and
              publish state.
            </li>
          ))}
          {s?.admin && (
            <>
              <li>
                <Link to="/datawiki-config/">datawiki-config</Link> — this
                instance's config, browsable as a repo.
              </li>
              <li>
                <Link to="/datawiki-config/repos">configure repos</Link> — the
                served set: add, remove, repoint, or hide one.
              </li>
              <li>
                <Link to="/datawiki-config/github">github connection</Link> — the
                shared push token.
              </li>
            </>
          )}
        </ul>
      </SystemSection>
      {s?.admin && (
        <SystemSection title="Admin">
          <ul className="dw-tools-list">
            <li>
              <Link to="/_access">access</Link> — groups, levels, and per-repo rules.
            </li>
            <li>
              <Link to="/_status">status</Link> — orchestrator: declared repos vs.
              what's actually served.
            </li>
            <li>
              <Link to="/_cleanup">cleanup</Link> — remove orphaned state (stub).
            </li>
            <li>
              <a href="/healthz" target="_blank" rel="noreferrer">
                healthz
              </a>{" "}
              — liveness + build metadata (JSON).
            </li>
          </ul>
        </SystemSection>
      )}
    </SystemPage>
  );
}
